// «Путь сделки» — сквозная цепочка от заявки до оплаты в одном экране.
//
// ЧЕГО НЕТ НИ НА ОДНОМ ПОРТАЛЕ. 55 видит закупку и отгрузку, 73 — верификации и отчёты
// инженеров, панель директора — деньги, Bitrix — карточки по отдельности. Вопрос «где эта
// работа встала» не задан нигде: чтобы на него ответить, надо было заглянуть в шесть
// смарт-процессов, в две таблицы портала 73, в состав сделки и в журнал переносов срока.
//
// ЧТО ЗДЕСЬ ГЛАВНОЕ. Не картинка цепочки, а первая строка: «Встало: счёт клиенту» — и рядом
// причина словами. Схема ниже нужна, чтобы человек проверил этот вывод, а не чтобы им
// любоваться. Если строка вывода исчезнет, экран потеряет весь смысл.
//
// НИ ОДНОГО СВОЕГО ПРАВИЛА ПРО ДАННЫЕ. «Оплачен ли счёт», «есть ли разрыв», «где встало» —
// всё считает ядро (chain_api.py) одним SQL и для карточки, и для счётчиков, и для списка.
// Здесь только показ; словарь состояний и подписи — в lib/chain.ts.
//
// ТРИ РАЗНЫХ ОТВЕТА ВМЕСТО ОДНОЙ ПУСТОТЫ — то, на чём держится доверие к экрану:
//   «есть» — записи нашлись; «нет» — проверили, записей нет (это разрыв);
//   «не знаем» — учёта в то время не вели, и отсутствие записи не говорит ничего;
//   «не сведено» — связи в ядре нет вовсе (реализация 1С).
// Пустая строка вместо «не знаем» превращала бы экран в обвинение людей в том, чего не было.

import { Fragment, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { useПутьСделки, useРазрывы, useСправочникиПути } from '../api/chain'
import { useRefs } from '../api/hooks'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import {
  ОКНА, РАЗМЕР_СТРАНИЦЫ, видыПоЧастоте, итогПути, классВажности, оСоставе, переносыЗадним,
  плюрал, состояние, сутьЗаписи,
} from '../lib/chain'
import type { Звено, ПутьСделки, Разрыв } from '../lib/chain'
import { fmtDate, fmtDays, fmtMoney, fmtText, pluralN } from '../lib/format'

/**
 * Запасной порог «зависло», пока `/chain/refs` не пришёл. Настоящий задаёт ядро
 * (`ЗАВИСЛО_ДНЕЙ` в chain_api.py), и на экране печатается именно он: здесь только значение
 * по умолчанию, чтобы фраза не осталась с дыркой на месте числа. Раньше «30» стояло тут
 * же голым числом рядом со словом «дней» — и при смене порога в ядре текст соврал бы.
 */
const ПОРОГ_ЗАВИСАНИЯ = 30

export default function DealChain() {
  const { id } = useParams<{ id: string }>()
  return id ? <Путь id={id} /> : <Обзор />
}

// ═══════════════════════════════ путь одной сделки ═══════════════════════════════

function Путь({ id }: { id: string }) {
  const q = useПутьСделки(id)
  const refs = useRefs()
  const п = q.data
  const итог = useMemo(() => итогПути(п), [п])

  return (
    <>
      <div className="page-head">
        <h1>Путь сделки #{id}</h1>
        <div className="spacer" />
        <Link to={`/deals/${id}`}>карточка сделки →</Link>
        {' · '}
        <Link to="/chain">где рвётся чаще →</Link>
      </div>

      <QueryState isLoading={q.isLoading} error={q.error}>
        {п && (
          <>
            <Вывод п={п} итог={итог} />
            <Цепочка п={п} refs={refs} />
            <Рядом п={п} />
            <Переносы п={п} />
            <ОДанных заметки={п.data_notes} />
          </>
        )}
      </QueryState>
    </>
  )
}

/**
 * Первая строка экрана и его единственный смысл: где встало и почему.
 *
 * Порядок частей выбран под пять секунд чтения: сначала вывод, потом сумма и клиент (чтобы
 * понять, стоит ли этим заниматься), потом остальные разрывы. Шапка сделки — ПОСЛЕ вывода:
 * название и стадию человек и так знает, он пришёл сюда за другим.
 */
function Вывод({ п, итог }: { п: ПутьСделки; итог: ReturnType<typeof итогПути> }) {
  const прочие = п.gaps.filter((g) => g.code !== п.stuck_at?.code)
  const состав = оСоставе(п)
  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <span className={итог.класс}>{итог.заголовок}</span>
          <span className="mono">{fmtMoney(п.deal.opportunity, п.deal.currency)}</span>
          <EntityLink kind="company" id={п.deal.company_id} title={п.deal.company_title} />
          {п.vessel && (
            <EntityLink kind="vessel" id={п.vessel.id} title={п.vessel.name} />
          )}
        </div>
        <div style={{ marginTop: 8 }}>{итог.пояснение}</div>

        {/* Остальные разрывы той же сделки. Обычно рвётся в нескольких местах, и чинить
            надо самое раннее — но знать про остальные человек должен сразу, а не после
            того, как починит первое. */}
        {prochieНеПусто(прочие) && (
          <ul className="plain-list" style={{ marginTop: 12 }}>
            {прочие.map((g) => (
              <li key={g.code}>
                <span className={классВажности(g.severity)}>{g.severity}</span>{' '}
                <b>{g.title}</b> <span className="muted">— {g.why}</span>
              </li>
            ))}
          </ul>
        )}

        {состав && (
          <div className="notice" style={{ marginTop: 12, marginBottom: 0 }}>
            <b>Состав сделки:</b> {состав}.{' '}
            <Link to={`/deals/${п.deal.id}`}>посмотреть позиции</Link>
          </div>
        )}
      </div>

      <div className="tiles" style={{ marginBottom: 16 }}>
        <div className="tile">
          <div className="t-label">Стадия сделки</div>
          <div className="t-sum" style={{ fontSize: 'inherit' }}>
            {fmtText(п.deal.stage_id)}
          </div>
          <div className="t-label">{п.deal.closed ? 'закрыта' : 'открыта'}</div>
        </div>
        <div className="tile">
          <div className="t-label">Заведена</div>
          <div className="t-sum" style={{ fontSize: 'inherit' }}>{fmtDate(п.deal.date_create)}</div>
          <div className="t-label">
            {п.deal.closedate ? `закрыта ${fmtDate(п.deal.closedate)}` : 'дата закрытия пуста'}
          </div>
        </div>
        <div className="tile">
          <div className="t-label">Этапов с записями</div>
          <div className="t-sum">
            {п.steps.filter((s) => s.state === 'есть').length} из {п.steps.length}
          </div>
          {/* «Не знаем» показываем ОТДЕЛЬНОЙ цифрой, а не вычитаем из «есть»: это не
              недостача, это границы наших данных. */}
          <div className="t-label">
            не знаем: {п.steps.filter((s) => s.state === 'не_знаем').length}
          </div>
        </div>
        <div className={`tile${п.gaps.some((g) => g.severity === 'разрыв') ? ' hot' : ''}`}>
          <div className="t-label">Разрывов</div>
          <div className="t-sum">{п.gaps.filter((g) => g.severity === 'разрыв').length}</div>
          <div className="t-label">
            наблюдений: {п.gaps.filter((g) => g.severity === 'наблюдение').length}
          </div>
        </div>
      </div>
    </>
  )
}

/** Мелкая обёртка, чтобы условие в разметке читалось словами, а не длиной массива. */
const prochieНеПусто = (g: Разрыв[]) => g.length > 0

// ─────────────────────────── сама цепочка ───────────────────────────

/**
 * Звенья по порядку цепочки, а не по дате. Порядок задаёт ядро (ЗВЕНЬЯ), и он же — порядок
 * поиска «где встало»: чинить надо самое раннее место, дальше по цепочке всё вторично.
 *
 * Дата у звена показана рядом, и по ней видно, когда порядок нарушен: счёт, выставленный
 * раньше АВР, — обычное дело, и экран это не прячет и не переставляет.
 */
function Цепочка({ п, refs }: { п: ПутьСделки; refs: ReturnType<typeof useRefs> }) {
  const [раскрыто, setРаскрыто] = useState<Record<string, boolean>>({})
  const разрывПо = useMemo(() => {
    const m: Record<string, Разрыв[]> = {}
    for (const g of п.gaps) (m[g.step] ||= []).push(g)
    return m
  }, [п.gaps])

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2>Цепочка</h2>
      <div className="muted" style={{ marginBottom: 12 }}>
        Порядок — как в цепочке, а не по дате: рядом видно, когда этапы шли не по порядку.
        «Не знаем» — это не пропуск, а граница данных: под строкой написано, почему.
      </div>
      {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Этап</th>
              <th>Состояние</th>
              <th className="num">Записей</th>
              <th>Когда</th>
              <th>Что именно</th>
            </tr>
          </thead>
          <tbody>
            {п.steps.map((з) => {
              const с = состояние(з.state)
              const свои = разрывПо[з.key] ?? []
              const встало = п.stuck_at?.step === з.key
              const открыто = !!раскрыто[з.key]
              return (
                // Fragment с key, а не голый <>: у каждого звена несколько строк таблицы
                // (сама строка, пояснение, разрывы, раскрытые записи), и React должен
                // различать их по звену, иначе при раскрытии перерисовывается вся таблица.
                <Fragment key={з.key}>
                  <tr>
                    <td data-label="Этап">
                      <b>{з.title}</b>
                      {встало && (
                        <>
                          {' '}
                          <span className="badge lose">здесь встало</span>
                        </>
                      )}
                      <div className="hint">{з.source}</div>
                    </td>
                    <td className="nowrap" data-label="Состояние">
                      <span className={с.класс} title={с.смысл}>{с.ярлык}</span>
                    </td>
                    <td className="num" data-label="Записей">
                      {з.count > 0 ? (
                        <button
                          type="button"
                          onClick={() => setРаскрыто({ ...раскрыто, [з.key]: !открыто })}
                        >
                          {з.count}
                        </button>
                      ) : <span className="muted">—</span>}
                    </td>
                    <td className="nowrap mono" data-label="Когда">{з.at ?? <span className="muted">—</span>}</td>
                    <td data-label="Что именно">
                      {/* Одна строка сути — по первой записи. Остальные раскрываются
                          кнопкой: у сделки бывает шесть АВР, и вываливать их все в таблицу
                          значило бы утопить главный вывод. */}
                      {з.items.length > 0
                        ? сутьЗаписи(з.key, з.items[0]) || <span className="muted">без подробностей</span>
                        : <span className="muted">—</span>}
                      {з.count > 1 && (
                        <span className="muted"> и ещё {з.count - 1}</span>
                      )}
                    </td>
                  </tr>

                  {/* Почему звено пустое — словами ядра. Это и есть разница между честным
                      экраном и пустой клеткой. */}
                  {(з.note || з.state === 'не_знаем' || з.state === 'не_сведено') && (
                    <tr>
                      <td colSpan={5} className="hint">
                        {з.note || с.смысл}
                        {з.since && з.state === 'не_знаем' && (
                          <> Этот учёт ведут с {з.since}
                            {п.deal.day ? `, а сделка датирована ${п.deal.day}` : ''}.</>
                        )}
                      </td>
                    </tr>
                  )}

                  {/* Заказы клиента в 1С — доказательство под звеном «Реализация в 1С».
                      Строка «заказ есть, а отгрузки нет» без самого заказа была бы
                      утверждением без основания, а это ровно тот вывод, за которым на
                      экран и приходят. */}
                  {з.key === 'realization_1c' && (з.orders ?? []).length > 0 && (
                    <tr>
                      <td colSpan={5} className="hint">
                        Заказы клиента в 1С:{' '}
                        {(з.orders ?? []).map((o) => [
                          `№ ${o.number ?? '—'}`,
                          o.at ? `от ${o.at}` : null,
                          o.plan_date ? `отгрузить к ${String(o.plan_date).slice(0, 10)}` : null,
                          o.posted === false ? 'не проведён' : null,
                        ].filter(Boolean).join(', ')).join('; ')}
                      </td>
                    </tr>
                  )}

                  {свои.map((g) => (
                    <tr key={`${з.key}-${g.code}`}>
                      <td colSpan={5}>
                        <span className={классВажности(g.severity)}>{g.severity}</span>{' '}
                        <b>{g.title}</b> <span className="muted">— {g.why}</span>
                      </td>
                    </tr>
                  ))}

                  {открыто && з.items.map((r, i) => (
                    <tr key={`${з.key}-item-${r.id ?? r.payment_id ?? i}`}>
                      {/* Раскрытые записи звена идут теми же колонками, что и строка звена:
                          порядковый номер шапки не имеет, остальные подписаны как в шапке. */}
                      <td className="muted" data-label="">{i + 1}.</td>
                      <td className="nowrap" data-label="Состояние">
                        {r.stage_id
                          ? (
                            <span className={`badge ${refs.itemStageKind(r.stage_id)}`}>
                              {refs.itemStageName(r.stage_id)}
                            </span>
                          )
                          : <span className="muted">—</span>}
                      </td>
                      <td className="num mono" data-label="Записей">{r.id ?? r.payment_id ?? ''}</td>
                      <td className="nowrap mono" data-label="Когда">{r.at ?? '—'}</td>
                      <td data-label="Что именно">
                        {сутьЗаписи(з.key, r) || fmtText(r.title)}
                        {/* Запись нашлась по своему полю-привязке, а не по родителю. У семи
                            платежей две привязки указывают на РАЗНЫЕ сделки, и такая запись
                            появится в пути обеих: молчать об этом нельзя. */}
                        {r.by_parent === false && (
                          <>
                            {' '}
                            <span className="badge" title="Найдено по полю-привязке записи, а не по родителю (parentId2). У части платежей эти две привязки указывают на разные сделки.">
                              по полю-привязке
                            </span>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─────────────────────────── рядом, но не по сделке ───────────────────────────

/**
 * Отдельный блок, а не строки цепочки, — и это принципиально. Акты на оборудование к сделке
 * не привязаны ВООБЩЕ: поле «номер сделки» пусто во всех 2 630 актах (на 28.07.2026),
 * единственная связь — IMO судна. Поставь их в цепочку, и «акт есть» прочиталось бы как
 * «акт по этой сделке», хотя у судна бывает десяток сделок в год.
 *
 * Реализация 1С стояла здесь же до 27.07.2026 — с пометкой «не сведено», потому что зеркала
 * документов 1С в ядре не было. Теперь оно есть, связь идёт через номер сделки Bitrix
 * в справочнике сделок 1С, и звено переехало в саму цепочку. Этот блок остался про то,
 * что к сделке действительно не привязано.
 */
function Рядом({ п }: { п: ПутьСделки }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2>Рядом, но не по сделке</h2>
      <div className="muted" style={{ marginBottom: 12 }}>
        Это НЕ звенья цепочки: к сделке они не привязаны. Показаны, чтобы было куда смотреть
        дальше, — но выводов о ходе этой сделки из них делать нельзя.
      </div>
      {п.nearby.map((н) => {
        const с = состояние(н.state)
        return (
          <div key={н.key} style={{ marginBottom: 12 }}>
            <div>
              <b>{н.title}</b> <span className={с.класс}>{с.ярлык}</span>
              {typeof н.total === 'number' && н.total > н.count && (
                <span className="muted"> — показано {н.count} из {н.total}</span>
              )}
            </div>
            <div className="hint">
              Связь: {н.link}. {н.why ?? ''} {н.note ?? ''}
            </div>
            {/* Судно заведено больше одного раза — акты могли прийти от другой карточки
                того же IMO. 361 номер IMO в зеркале имеет по нескольку записей. */}
            {н.key === 'equip_acts' && п.vessel && п.vessel.imo_rows > 1 && (
              <div className="notice" style={{ marginTop: 8, marginBottom: 0 }}>
                Судно с IMO {п.vessel.imo} заведено в CRM{' '}
                {pluralN(п.vessel.imo_rows, 'раз', 'раза', 'раз')}. Акты
                найдены по номеру IMO, поэтому часть из них может относиться к другой карточке
                того же судна.
              </div>
            )}
            {н.items.length > 0 && (
              <ul className="plain-list" style={{ marginTop: 8 }}>
                {н.items.map((r, i) => (
                  <li key={r.id ?? i}>
                    <span className="mono">{сутьЗаписи(н.key, r)}</span>
                    {r.vessel_name && <span className="muted"> · {r.vessel_name}</span>}
                    {r.status && <span className="muted"> · {r.status}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })}
      {п.p73_requests.length > 0 && (
        <div>
          <b>Заявки портала 73</b>
          <ul className="plain-list" style={{ marginTop: 8 }}>
            {п.p73_requests.map((з) => (
              <li key={з.id}>
                <span className="mono">#{з.id}</span> {fmtText(з.title)}
                <span className="muted">
                  {' '}· {fmtText(з.status)}{з.location ? ` · ${з.location}` : ''}
                  {' '}· создана {fmtDate(з.created)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────── переносы срока оплаты ───────────────────────────

/**
 * Журнал переносов «примерной даты оплаты». В Bitrix остаётся ТОЛЬКО последнее значение —
 * историю оттуда не добыть, и без этого журнала перенос задним числом невидим: долг
 * выглядит свежим. Пустой список здесь НЕ означает «не переносили»: журнал ведут
 * с 08.07.2026, и про более старые переносы никто не знает.
 */
function Переносы({ п }: { п: ПутьСделки }) {
  if (п.due_moves.length === 0) return null
  const задним = переносыЗадним(п)
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2>Срок оплаты переносили</h2>
      <div className="muted" style={{ marginBottom: 12 }}>
        {п.due_moves.length} {плюрал(п.due_moves.length, 'перенос', 'переноса', 'переносов')}
        {задним.length > 0 && (
          <>
            , из них {задним.length} — <b>после того, как срок уже наступил</b>. Такой перенос
            прячет просрочку: в Bitrix виден только последний срок
          </>
        )}
        . В Bitrix история не хранится — она есть только в журнале ядра, и вести его начали
        08.07.2026: отсутствие старых переносов означает «не наблюдали», а не «не было».
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Платёж</th>
              <th>Было</th>
              <th>Стало</th>
              <th>Когда перенесли</th>
              <th>Задним числом</th>
            </tr>
          </thead>
          <tbody>
            {п.due_moves.map((m, i) => (
              <tr key={`${m.payment_id}-${m.changed_at ?? i}`}>
                <td className="mono" data-label="Платёж">#{m.payment_id}</td>
                <td className="nowrap mono" data-label="Было">{fmtText(m.old)}</td>
                <td className="nowrap mono" data-label="Стало">{fmtText(m.new)}</td>
                <td className="nowrap" data-label="Когда перенесли">{fmtDate(m.changed_at, true)}</td>
                <td data-label="Задним числом">
                  {m.after_due
                    ? <span className="badge lose">да</span>
                    : <span className="muted">нет</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─────────────────────────── чего в данных нет ───────────────────────────

/**
 * Заметки ядра о том, чего в данных НЕТ. Единственное место, где экран сам рассказывает
 * о своих слепых пятнах. Спрятать их под «подробнее» — значит сделать вид, что цепочка
 * знает всё; а она не знает про реализацию 1С, про привязку актов и про старые переносы.
 */
function ОДанных({ заметки }: { заметки: Record<string, string> }) {
  const [открыто, setОткрыто] = useState(false)
  const ключи = Object.keys(заметки)
  if (ключи.length === 0) return null
  return (
    <div className="card">
      <h2>
        Чего в этих данных нет{' '}
        <button type="button" onClick={() => setОткрыто(!открыто)}>
          {открыто ? 'скрыть' : `показать (${ключи.length})`}
        </button>
      </h2>
      <div className="muted">
        Границы честности этого экрана. Их считает ядро, а не мы: каждая строка — про то,
        какой вывод по этим данным сделать НЕЛЬЗЯ.
      </div>
      {открыто && (
        <ul className="plain-list" style={{ marginTop: 12 }}>
          {ключи.map((k) => (
            <li key={k}>
              <span className="mono muted">{k}</span> — {заметки[k]}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ═══════════════════════════════ обзор: где рвётся чаще ═══════════════════════════════

/**
 * Второй вопрос после «где встала эта сделка» — «а где рвётся чаще всего». Числа считает
 * ядро тем же SQL, что отбирает строки: счётчик и список не могут разойтись по построению.
 *
 * Окно обязательно и всегда показано. Без него «сделка выиграна, счёта нет» набирает
 * 2 159 сделок по всему зеркалу против 43 за квартал (на 28.07.2026) — и почти всё это
 * работа, законченная до появления смарт-процессов. Число без окна тут не просто
 * бесполезно, оно врёт.
 */
function Обзор() {
  const [sp, setSp] = useSearchParams()
  const вид = sp.get('kind') || ''
  const дней = Number(sp.get('days') || 90)
  const [start, setStart] = useState(0)

  const refs = useСправочникиПути(дней)
  const список = useРазрывы({ kind: вид || undefined, days: дней, start,
                              limit: РАЗМЕР_СТРАНИЦЫ })
  const виды = useMemo(() => видыПоЧастоте(refs.data?.gap_kinds ?? []), [refs.data])
  const выбрать = (k: string) => {
    const next = new URLSearchParams(sp)
    if (k) next.set('kind', k)
    else next.delete('kind')
    setStart(0)
    setSp(next, { replace: true })
  }
  const окно = (d: number) => {
    const next = new URLSearchParams(sp)
    next.set('days', String(d))
    setStart(0)
    setSp(next, { replace: true })
  }

  const данные = список.data
  const выбранный = виды.find((v) => v.code === вид)

  return (
    <>
      <div className="page-head">
        <h1>Путь сделки</h1>
        <span className="muted">
          {refs.data ? `сделок в окне: ${refs.data.window.deals}` : ''}
        </span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="chain">
          <div className="f">
            <label htmlFor="chw">Окно по дате закрытия</label>
            <select id="chw" value={дней} onChange={(e) => окно(Number(e.target.value))}>
              {ОКНА.map((о) => (
                <option key={о.дней} value={о.дней}>{о.ярлык} ({fmtDays(о.дней)})</option>
              ))}
            </select>
          </div>
          <div className="f" style={{ flex: 2, minWidth: 260 }}>
            <label htmlFor="chk">Вид разрыва</label>
            <select id="chk" value={вид} onChange={(e) => выбрать(e.target.value)}>
              <option value="">любой разрыв (без наблюдений)</option>
              {виды.map((v) => (
                <option key={v.code} value={v.code}>
                  {/* «—» вместо нуля у невычислимого вида: у own-ветки нет данных
                      судить о нём, и ноль здесь читался бы как «разрывов нет». */}
                  {v.title} — {v.computable === false ? '—' : (v.n ?? 0)}
                  {v.severity === 'наблюдение' ? ' (наблюдение)' : ''}
                </option>
              ))}
            </select>
          </div>
        </FilterBar>
        <div className="muted" style={{ marginTop: 8 }}>
          Окно — по дате закрытия сделки (у незакрытых по последнему изменению). Оно есть
          всегда и специально: смарт-процессы ведут с 2023–2024 годов, и на всём зеркале
          «счёта нет» показало бы весь архив, где счетов и быть не могло. Порог «зависло» —
          {' '}{fmtDays(refs.data?.window.hang_days ?? ПОРОГ_ЗАВИСАНИЯ)}, это наше
          соглашение, а не свойство данных.
        </div>
      </div>

      <QueryState isLoading={refs.isLoading} error={refs.error}>
        <div className="tiles" style={{ marginBottom: 16 }}>
          {виды.filter((v) => v.severity === 'разрыв').map((v) => (
            <button
              type="button"
              key={v.code}
              className={`tile${v.code === вид ? ' hot' : ''}`}
              style={{ textAlign: 'left', cursor: 'pointer' }}
              onClick={() => выбрать(v.code === вид ? '' : v.code)}
              title={v.why}
            >
              <div className="t-label">{v.title}</div>
              <div className="t-sum">{v.computable === false ? '—' : (v.n ?? 0)}</div>
              <div className="t-label">{v.step}</div>
            </button>
          ))}
        </div>
      </QueryState>

      {выбранный && (
        <div className="notice">
          <b>{выбранный.title}.</b> {выбранный.why}
        </div>
      )}

      <QueryState isLoading={список.isLoading} error={список.error}>
        <div className="card">
          <div className="muted" style={{ marginBottom: 12 }}>
            {данные
              ? `Найдено ${данные.total} ${плюрал(данные.total, 'сделка', 'сделки', 'сделок')}.`
              : ''}
            {' '}Сначала самые дорогие: список нужен, чтобы начать с денег, а не с даты.
          </div>
          {данные && данные.rows.length === 0 && (
            <div className="empty">
              <div className="empty-title">В этом окне разрывов нет</div>
              <div className="empty-hint">
                Это ответ про окно, а не про всю базу: расширьте окно, чтобы посмотреть глубже.
              </div>
            </div>
          )}
          {данные && данные.rows.length > 0 && (
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Сделка</th>
                    <th className="num">Сумма</th>
                    <th>Стадия</th>
                    <th>Где встало</th>
                    <th>Все разрывы</th>
                    <th>Закрыта</th>
                  </tr>
                </thead>
                <tbody>
                  {данные.rows.map((r) => (
                    <tr key={r.deal_id}>
                      <td data-label="Сделка">
                        <Link to={`/chain/${r.deal_id}`}>#{r.deal_id}</Link>{' '}
                        <span className="muted">{fmtText(r.title)}</span>
                      </td>
                      <td className="num mono nowrap" data-label="Сумма">
                        {fmtMoney(r.opportunity, r.currency ?? 'RUB')}
                      </td>
                      <td className="nowrap" data-label="Стадия">
                        <span className={`badge ${refs_класс(r.stage_code)}`}>
                          {fmtText(r.stage_code)}
                        </span>
                      </td>
                      <td className="nowrap" data-label="Где встало">
                        {r.stuck_at
                          ? <b>{r.stuck_at.title}</b>
                          : <span className="muted">—</span>}
                      </td>
                      <td data-label="Все разрывы">
                        {/* Все разрывы строки, а не только запрошенный: сделка обычно
                            рвётся в нескольких местах, и починка первого не закончит дело. */}
                        {r.gaps.map((код) => {
                          const v = виды.find((x) => x.code === код)
                          return (
                            <span key={код} className={классВажности(v?.severity)}
                                  style={{ marginRight: 4 }} title={v?.why}>
                              {v?.title ?? код}
                            </span>
                          )
                        })}
                      </td>
                      <td className="nowrap" data-label="Закрыта">{fmtDate(r.closedate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {данные && (данные.next !== null || start > 0) && (
            <div className="pager">
              <button type="button" disabled={start === 0}
                      onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
                ← назад
              </button>
              <span className="muted">
                {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, данные.total)} из {данные.total}
              </span>
              <button type="button" disabled={данные.next === null}
                      onClick={() => setStart(данные.next ?? start)}>
                вперёд →
              </button>
            </div>
          )}
        </div>
      </QueryState>

      {refs.data && <Источники refs={refs.data} />}
      {refs.data && <ОДанных заметки={refs.data.data_notes} />}
    </>
  )
}

/**
 * Класс ярлыка стадии сделки по её КОДУ. Справочник стадий здесь не спрашиваем: в списке
 * разрывов ядро отдаёт только код, а тянуть /refs ради раскраски одной колонки — лишний
 * запрос на каждое открытие. WON и LOSE — единственные коды, о смысле которых можно судить
 * без справочника; всё прочее нейтрально, и это честно.
 */
function refs_класс(код?: string | null): string {
  if (код === 'WON') return 'won'
  if (код === 'LOSE' || код === 'APOLOGY') return 'lose'
  return 'work'
}

/**
 * Из чего собрана цепочка и с какого дня ведут каждое звено. Это не украшение: именно эти
 * даты объясняют, почему у старой сделки половина цепочки в состоянии «не знаем».
 */
function Источники({ refs }: { refs: NonNullable<ReturnType<typeof useСправочникиПути>['data']> }) {
  return (
    <div className="card" style={{ marginTop: 16, marginBottom: 16 }}>
      <h2>Из чего собрана цепочка</h2>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Этап</th>
              <th>Источник</th>
              <th>Привязка к сделке</th>
              <th>Ведут с</th>
              <th className="num">Записей</th>
              <th className="num">Сделок</th>
            </tr>
          </thead>
          <tbody>
            {refs.steps.map((з: Звено) => (
              <tr key={з.key}>
                <td data-label="Этап">{з.title}</td>
                <td className="muted" data-label="Источник">{з.source}</td>
                <td className="muted" data-label="Привязка к сделке">{з.link}</td>
                <td className="nowrap mono" data-label="Ведут с">
                  {з.since ?? <span className="muted">—</span>}
                </td>
                <td className="num mono" data-label="Записей">{з.rows ?? ''}</td>
                <td className="num mono" data-label="Сделок">{з.deals ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="muted" style={{ marginTop: 8 }}>
        «Ведут с» — день первой записи этого типа в зеркале. Если сделка старше этого дня,
        отсутствие записи ничего не означает, и экран говорит «не знаем», а не «нет».
      </div>
    </div>
  )
}
